import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { CJEvent } from 'types/event';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import request from 'utils/request';
import { format } from 'utils/time/format';
import { EVENTS_KEY } from 'containers/CahierJournal/queries/useCJSeances';
import groupBy from 'utils/arrays/groupBy';
import { deserializeEvent } from 'containers/CahierJournal/queries/serializer';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import useNotification from 'containers/App/Notifications/useNotification';
import { useTranslation } from 'react-i18next';

interface Props {
  ids: string[];
  schoolyearId?: string;
  schoolId?: string;
}

interface ContextItem {
  day: string;
  previousEvents: CJEvent[];
}

function restoreCJ({
  schoolyearId,
  schoolId,
  ids,
  location,
}: {
  schoolyearId: string;
  schoolId: string;
  ids: string[];
  location: string;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/cahier-journal/seances/restore`;
  return request(requestURL, {
    method: 'PATCH',
    body: { ids },
  }).then(p => (p.data || []).map(d => deserializeEvent(d, location)));
}

export function useRestoreCJSeances() {
  const { schoolyear, school } = useSchoolSchoolyear();
  const { notifyError } = useNotification();
  const user = useUser();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['cahierJournal']);
  const { mutateAsync } = useMutation<any, any, Props, ContextItem[]>({
    mutationFn: ({ ids, schoolyearId, schoolId }) =>
      restoreCJ({
        schoolyearId: schoolyearId || schoolyear.id,
        schoolId: schoolId || school.id,
        ids,
        location: user.location,
      }),
    retry: 1,
    onSuccess: cjEvents => {
      Object.entries(groupBy(cjEvents, e => format(e.start, 'fr-FR'))).map(
        ([d, newEvents]) => {
          const newEventsIds = newEvents.map(e => e.id);
          const previousEvents = queryClient.getQueryData<CJEvent[]>([
            schoolyear.id,
            school.id,
            EVENTS_KEY,
            d,
          ]);
          queryClient.setQueryData<CJEvent[]>(
            [schoolyear.id, school.id, EVENTS_KEY, d],
            old => [
              // le filter c'est pour gérer le cas d'un séance qui fail à être ajouté
              // mais ça fonctionne au retry
              ...(old || []).filter(e => !newEventsIds.includes(e.id)),
              ...newEvents,
            ],
          );
          return { day: d, previousEvents: previousEvents || [] };
        },
      );
    },
    onError: (err, { ids }) => {
      notifyError({
        title: t('cahierJournal:messages.createError', {
          count: (ids || []).length,
        }),
      });
      throw err;
    },
  });
  return mutateAsync;
}
